import { isBusiness250Plan } from '@/shared/lib/billing/isBusiness250Plan';
import { isPaidSubscription } from '@/shared/lib/billing/isPaidSubscription';
import {
  PaymentInterval,
  Plan,
  PlanCategory,
  Products,
  Subscription,
} from '@/shared/types/Billing';
import { Credits } from '@/shared/types/User';

import {
  UpsellAnnualOffer,
  UpsellBannerState,
  UpsellCategory,
  UpsellContext,
  UpsellOffer,
  UpsellTierOffer,
  UsageOfferSelection,
} from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

const UPSELL_ANNUAL_BANNER_FROM_DAY = 14;
export const UPSELL_WELCOME_WINDOW_DAYS = 7;

const NEXT_CATEGORY: Partial<Record<UpsellCategory, UpsellCategory>> = {
  [PlanCategory.Creator]: PlanCategory.CreatorPro,
  [PlanCategory.CreatorPro]: PlanCategory.Business,
};

const UPSELL_CATEGORIES = new Set<PlanCategory>([PlanCategory.Business, PlanCategory.Creator, PlanCategory.CreatorPro]);

const isUpsellCategory = (category: PlanCategory): category is UpsellCategory => UPSELL_CATEGORIES.has(category);

const getPlanMinutes = (plan: Plan) => Number(plan.metadata.bundle_value) || 0;

const findActivePrice = (plan: Plan, interval: PaymentInterval) =>
  plan.prices.find((price) => price.status === 'active' && price.recurring?.interval === interval);

const getPeriodMinutes = (monthlyMinutes: number, interval: PaymentInterval) =>
  interval === PaymentInterval.Year ? monthlyMinutes * 12 : monthlyMinutes;

const isUpsellSubscription = (subscription: null | Subscription | undefined): subscription is Subscription =>
  Boolean(
    subscription &&
      isPaidSubscription(subscription) &&
      !subscription.cancel_at_period_end &&
      !subscription.schedule_info?.plan,
  );

const isExcludedPlan = (grid: string, plan: Plan) =>
  grid === '0' && plan.category === PlanCategory.Business && getPlanMinutes(plan) === 500;

const resolveTierOffer = (
  plans: Plan[],
  currentMinutes: number,
  category: UpsellCategory,
  interval: PaymentInterval,
  grid: string,
): null | UpsellTierOffer => {
  const targetCategory = NEXT_CATEGORY[category];

  if (!targetCategory || currentMinutes <= 0) return null;
  if (grid === '0' && category === PlanCategory.CreatorPro && interval === PaymentInterval.Year) return null;

  const target = plans
    .filter(
      (plan) =>
        plan.category === targetCategory &&
        !plan.is_archived &&
        getPlanMinutes(plan) > 0 &&
        getPlanMinutes(plan) >= currentMinutes &&
        !(grid === '0' && isBusiness250Plan(plan)) &&
        findActivePrice(plan, interval),
    )
    .sort((a, b) => getPlanMinutes(a) - getPlanMinutes(b))[0];

  const price = target && findActivePrice(target, interval);

  if (!target || !price) return null;

  return {
    amount: price.unit_amount,
    category: targetCategory,
    currency: price.currency,
    currentMinutes: getPeriodMinutes(currentMinutes, interval),
    interval,
    kind: 'tier',
    plan: target,
    price,
    targetMinutes: getPeriodMinutes(getPlanMinutes(target), interval),
  };
};

const resolveAnnualOffer = (
  currentPlan: Plan,
  category: UpsellCategory,
  currentPriceId: string,
): null | UpsellAnnualOffer => {
  const monthlyPrice = findActivePrice(currentPlan, PaymentInterval.Month);
  const annualPrice = findActivePrice(currentPlan, PaymentInterval.Year);
  const minutes = getPlanMinutes(currentPlan);

  if (!monthlyPrice || !annualPrice || !minutes || monthlyPrice.id !== currentPriceId) return null;

  const savings = monthlyPrice.unit_amount * 12 - annualPrice.unit_amount;

  if (savings <= 0) return null;

  return {
    amount: annualPrice.unit_amount,
    category,
    currency: annualPrice.currency,
    interval: PaymentInterval.Year,
    kind: 'annual',
    plan: currentPlan,
    pool: minutes * 12,
    price: annualPrice,
    savings,
  };
};

type ResolveUpsellContextParams = {
  products: Products | undefined;
  subscription: null | Subscription | undefined;
};

export const resolveUpsellContext = ({ products, subscription }: ResolveUpsellContextParams): null | UpsellContext => {
  const grid = products?.pricing_experiment?.grid;

  if (!products || !grid || !isUpsellSubscription(subscription)) return null;

  const currentPlan = products.plan.find((plan) => plan.id === subscription.plan.product_id);

  if (!currentPlan || !isUpsellCategory(currentPlan.category) || isExcludedPlan(grid, currentPlan)) return null;

  const { category } = currentPlan;
  const { interval } = subscription.plan;
  const monthlyMinutes = getPlanMinutes(currentPlan) || subscription.credits.minutes;

  return {
    annual: interval === PaymentInterval.Month ? resolveAnnualOffer(currentPlan, category, subscription.plan.id) : null,
    category,
    currentPlan,
    grid,
    interval,
    monthlyMinutes,
    tier: resolveTierOffer(products.plan, monthlyMinutes, category, interval, grid),
  };
};

const findPlanByPrice = (products: Products, priceId: string) =>
  products.plan.find((plan) => plan.prices.some((price) => price.id === priceId));

export const isPlanUpgrade = (products: Products | undefined, fromPriceId: string, toPriceId: string) => {
  const from = products && findPlanByPrice(products, fromPriceId);
  const to = products && findPlanByPrice(products, toPriceId);

  if (!from || !to) return false;

  const fromMinutes = getPlanMinutes(from);
  const toMinutes = getPlanMinutes(to);
  const intervalOf = (plan: Plan, priceId: string) =>
    plan.prices.find((price) => price.id === priceId)?.recurring?.interval;

  return (
    toMinutes > fromMinutes ||
    (toMinutes === fromMinutes &&
      intervalOf(from, fromPriceId) === PaymentInterval.Month &&
      intervalOf(to, toPriceId) === PaymentInterval.Year)
  );
};

export const getWelcomeOffer = (context: UpsellContext): null | undefined | UpsellTierOffer => {
  if (context.grid !== 'D') return undefined;
  if (context.category === PlanCategory.Business) return null;

  return context.tier ?? undefined;
};

export const getDownloadOffer = (context: UpsellContext): null | UpsellOffer =>
  context.category === PlanCategory.Business ? context.annual : context.tier;

export const getUsageOffer = (context: UpsellContext, isFirstMonth: boolean): null | UsageOfferSelection => {
  const { annual, tier } = context;

  if (context.interval === PaymentInterval.Year) {
    return tier ? { primary: tier, secondary: null } : null;
  }

  const [first, second] = context.category === PlanCategory.Business || !isFirstMonth ? [annual, tier] : [tier, annual];
  const primary = first ?? second;

  return primary ? { primary, secondary: first ? second : null } : null;
};

export type UpsellUsage = {
  isCrossed: boolean;
  left: number;
  percent: number;
  periodKey: string;
  total: number;
  used: number;
};

export type UpsellUsageBase = { period: string; used: number };

export const getUpsellUsage = (
  subscription: Subscription,
  credits: Pick<Credits, 'minutes'> | undefined,
  base: undefined | UpsellUsageBase,
): null | UpsellUsage => {
  const periodKey = subscription.current_period_start;

  if (!credits || base?.period !== periodKey) return null;

  const total = credits.minutes.total - base.used;
  const used = Math.max(0, credits.minutes.used - base.used);

  if (total <= 0) return null;

  return {
    isCrossed: used * 2 >= total,
    left: Math.max(0, total - used),
    percent: Math.min(100, Math.floor((used / total) * 100)),
    periodKey,
    total,
    used,
  };
};

export const getDaysSince = (date: string, now: number) => Math.floor((now - new Date(date).getTime()) / DAY_MS);

export const isWithinFirstMonth = (date: string, now: number) => {
  const start = new Date(date);
  const year = start.getUTCFullYear();
  const nextMonth = start.getUTCMonth() + 1;
  const lastDay = new Date(Date.UTC(year, nextMonth + 1, 0)).getUTCDate();
  const monthLater = new Date(start);
  monthLater.setUTCFullYear(year, nextMonth, Math.min(start.getUTCDate(), lastDay));

  return now < monthLater.getTime();
};

type BannerStateParams = {
  context: null | UpsellContext;
  daysSincePurchase: number;
  hasSeenDownloadOffer: boolean;
  isFirstMonth: boolean;
  priceId: string;
  usage: null | UpsellUsage;
};

export const getUpsellBannerState = ({
  context,
  daysSincePurchase,
  hasSeenDownloadOffer,
  isFirstMonth,
  priceId,
  usage,
}: BannerStateParams): null | UpsellBannerState => {
  if (!context) return null;

  if (usage?.isCrossed) {
    const selection = getUsageOffer(context, isFirstMonth);

    return selection
      ? { id: `usage:${usage.periodKey}:${selection.primary.kind}`, offer: selection.primary, reason: 'usage' }
      : null;
  }

  if (context.interval === PaymentInterval.Year) return null;

  if (daysSincePurchase >= UPSELL_ANNUAL_BANNER_FROM_DAY) {
    if (context.annual) return { id: `annual:${priceId}`, offer: context.annual, reason: 'annual' };
    if (!isFirstMonth) return null;
  }

  if (context.category === PlanCategory.Business) {
    return hasSeenDownloadOffer && context.annual
      ? { id: `annual:${priceId}`, offer: context.annual, reason: 'annual' }
      : null;
  }

  return context.tier ? { id: `tier:${priceId}`, offer: context.tier, reason: 'tier' } : null;
};
